"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { getSiteUiCopy } from "@/lib/site-ui-copy";
import { blogLocaleFromPathname } from "@/lib/blog/locales";
import { localeFromPathname } from "@/lib/i18n/paths";
import { requestStaleTabRecovery } from "@/lib/stale-tab-recovery";

import { useSiteChromeLock } from "@/components/layout/CcSiteChromeLock";
import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";

type ErrorPageProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

export default function ErrorPage({ error, reset }: ErrorPageProps) {
  const pathname = usePathname() ?? "/";
  const copy = getSiteUiCopy(blogLocaleFromPathname(pathname) ?? localeFromPathname(pathname)).error;
  useSiteChromeLock(true);
  const reportedRef = useRef(false);

  useEffect(() => {
    requestStaleTabRecovery(error);
    if (reportedRef.current) return;
    reportedRef.current = true;

    const payload = {
      kind: "app_error_boundary",
      message: error.message,
      digest: error.digest,
      stack: error.stack,
      pathname: window.location.pathname,
      search: window.location.search,
    };

    console.error("[cc] app error boundary", payload);

    void fetch("/api/client-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // ignore — user may be offline
    });
  }, [error]);

  return (
    <main
      className="cc-main cc-error-fullscreen"
      role="alert"
      aria-labelledby="cc-error-title"
    >
      <div className="cc-error-fullscreen__inner">
        <div className="cc-error-fullscreen__icon" aria-hidden="true">
          <span className="cc-error-fullscreen__icon-mark">!</span>
        </div>

        <CcSanjayaEyebrow as="p" theme="on-dark" className="cc-error-fullscreen__eyebrow">
          {copy.eyebrow}
        </CcSanjayaEyebrow>

        <header className="cc-error-fullscreen__header">
          <h1 id="cc-error-title" className="cc-error-fullscreen__title">
            {copy.title}
          </h1>
          <p className="cc-error-fullscreen__lead">
            {copy.lead}
          </p>
        </header>

        <div className="cc-error-fullscreen__actions">
          <CcButton
            type="button"
            variant="primary"
            label={copy.reload}
            onClick={() => window.location.reload()}
          />
          <CcButton
            type="button"
            variant="outline"
            className="cc-error-fullscreen__action-secondary"
            label={copy.retry}
            onClick={() => reset()}
          />
        </div>

        {error.digest ? (
          <p className="cc-error-fullscreen__digest">
            <span className="cc-error-fullscreen__digest-label">{copy.reference}</span>
            <code>{error.digest}</code>
          </p>
        ) : null}
      </div>
    </main>
  );
}
